import { Error as ErrorIcon } from '@mui/icons-material';
import { Fade, Grid } from '@mui/material';
import { RoutalLoader, RoutalPalette } from '@nx-smartmonkey/ui';
import React, { useEffect, useState } from 'react';

import { Driver } from '../../../../../domain/driver/Driver';
import { GeoFence } from '../../../../../domain/geoFence/GeoFence';
import { PlanFlat } from '../../../../../domain/plan/PlanFlat';
import { useRetrieveOrganization } from '../../../../../hooks/organization/useRetrieveOrganization';
import { useRetrieveActiveProject } from '../../../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveRightPanelInfo } from '../../../../../hooks/right-panel/useRetrieveRightPanelInfo';
import { useRetrieveStop } from '../../../../../hooks/stop/useRetrieveStop';
import { useRetrieveStopDelivery } from '../../../../../hooks/stop/useRetrieveStopDelivery';
import Text from '../../../../../react-components/Text';
import ProblemsTooltip from '../../../plan-details/components/ProblemsTooltip';
import { StyledRightPanel } from '../StyledRightPanel';
import { StopBody } from './StopBody';
import { StopHeader } from './StopHeader';

interface TagButtonProps {
  isActive: boolean;
  textIntlId: string;
  onClick: (...args: any[]) => any;
}

const TagButton = ({ isActive, textIntlId, onClick }: TagButtonProps) => {
  const [hover, setHover] = useState<boolean>(false);

  let bgColor = RoutalPalette.grey12;
  if (hover) {
    bgColor = RoutalPalette.grey9;
  }
  if (isActive) {
    bgColor = RoutalPalette.neutral00;
  }

  return (
    <Grid
      container
      alignItems="center"
      justifyContent="center"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => {
        if (!isActive) {
          onClick();
        }
      }}
      style={{
        width: `80px`,
        height: `30px`,
        backgroundColor: bgColor,
        borderTopLeftRadius: `2px`,
        borderTopRightRadius: `2px`,

        ...(!isActive
          ? {
              cursor: `pointer`,
              borderBottom: `1px solid ${RoutalPalette.neutral10}`,
            }
          : {
              borderTop: `1px solid  ${RoutalPalette.neutral10}`,
              borderLeft: `1px solid ${RoutalPalette.neutral10}`,
              borderRight: `1px solid ${RoutalPalette.neutral10}`,
            }),
      }}
    >
      <Text
        tagName="span"
        intlId={textIntlId}
        style={{
          color: RoutalPalette.grey1,
          fontWeight: 400,
          cursor: !isActive ? `pointer` : `default`,
        }}
      />
    </Grid>
  );
};

type StopRightPanelProps = {
  selectedStopsIds?: Array<string>;
  validatePlan?: boolean;
  stopActionsMenu: React.ReactElement;
  plan?: PlanFlat;
  planDrivers?: Driver[];
  planGeoFences?: GeoFence[];
};

export const StopRightPanel = ({
  selectedStopsIds = [],
  validatePlan = false,
  stopActionsMenu,
  plan,
  planDrivers,
  planGeoFences,
}: StopRightPanelProps) => {
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: organization } = useRetrieveOrganization();

  const { detailId: stopId, detailType, setRightPanelInfo, cleanRightPanelInfo } = useRetrieveRightPanelInfo();

  const { data: stop, isLoading, error: retrieveStopError } = useRetrieveStop({ showErrorMessage: false });

  const { data: delivery, isLoading: isDeliveryLoading } = useRetrieveStopDelivery({
    showErrorMessage: false,
    pickupId: stop?.type === `pickup` ? stop.id : undefined,
  });

  const onClose = () => {
    cleanRightPanelInfo();
  };

  const isOpen = () => detailType === `stop` && !!stopId;

  const isSelected = () => Boolean(selectedStopsIds.find((id) => id === stopId));

  useEffect(() => {
    if (retrieveStopError) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retrieveStopError]);

  useEffect(() => {
    if (validatePlan && stop && stop.plan_id !== plan?.id) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan?.id, stop, validatePlan]);

  return (
    <StyledRightPanel
      key={stopId}
      style={{
        zIndex: 450,
        backgroundColor: RoutalPalette.neutral00,
        borderLeft: `1px solid ${RoutalPalette.neutral10}`,
        position: `relative`,
      }}
    >
      <Fade in={isOpen()} mountOnEnter>
        <Grid style={{ height: `100%` }}>
          {stop && (stop.pickup || (stop.type === `pickup` && delivery)) ? (
            <Grid
              container
              direction="row"
              style={{
                transform: `rotate(-90deg)`,
                position: `absolute`,
                left: `-96px`,
                width: `unset`,
                top: `128px`,
              }}
            >
              <TagButton
                textIntlId="pickup"
                isActive={stop.type === `pickup`}
                onClick={() => {
                  setRightPanelInfo({ detailId: `${stop.pickup!.id}` });
                }}
              />
              <div style={{ width: `2px` }} />
              <TagButton
                textIntlId="delivery"
                isActive={Boolean(stop.pickup)}
                onClick={() => {
                  setRightPanelInfo({ detailId: `${delivery!.id}` });
                }}
              />
            </Grid>
          ) : null}

          <Grid
            container
            direction="column"
            style={{
              width: `400px`,
              height: `100%`,
              padding: `0`,
            }}
            wrap="nowrap"
          >
            {isLoading || isDeliveryLoading ? (
              <Grid
                container
                justifyContent="center"
                style={{ height: `100%`, overflow: `hidden` }}
                alignItems="center"
              >
                <RoutalLoader width={60} />
              </Grid>
            ) : (
              <>
                {/* --- PANEL HEADER */}
                {stop && (
                  <>
                    <StopHeader
                      stop={stop}
                      warning={
                        <ProblemsTooltip type="stop" stop={stop} planDrivers={planDrivers} tooltipPlacement="topRight">
                          <Grid container>
                            <ErrorIcon style={{ color: RoutalPalette.warning.medium, fontSize: `24px` }} />
                          </Grid>
                        </ProblemsTooltip>
                      }
                      onClose={onClose}
                      options={React.createElement(
                        stopActionsMenu.type,
                        {
                          ...stopActionsMenu.props,
                          stops: [stop],
                        },
                        null
                      )}
                      planDrivers={planDrivers}
                      isSelected={isSelected()}
                    />

                    {activeProject && organization ? (
                      <Grid item container style={{ height: `calc(100% - 60px)`, overflowY: `auto` }}>
                        <div style={{ width: `100%`, height: `100%` }}>
                          <StopBody
                            stop={stop}
                            planDrivers={planDrivers ?? []}
                            activeProject={activeProject}
                            plan={plan}
                            planGeoFences={planGeoFences}
                          />
                        </div>
                      </Grid>
                    ) : null}
                  </>
                )}
              </>
            )}
          </Grid>
        </Grid>
      </Fade>
    </StyledRightPanel>
  );
};
